import React from 'react';
import { 
  Building2, 
  BarChart3, 
  Users, 
  Map, 
  ShieldCheck, 
  Zap, 
  ArrowUpRight, 
  Activity, 
  Database, 
  Globe, 
  FileText, 
  TrendingUp,
  AlertTriangle,
  Lock
} from 'lucide-react';
import { motion } from 'motion/react';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer, 
  LineChart, 
  Line,
  Cell
} from 'recharts';

interface PartnershipHubProps {
  agencyName: string;
  onClose: () => void;
}

const trafficHotspots = [
  { area: 'Lekki Toll', incidents: 42, severity: 'High' },
  { area: 'Ikorodu Rd', incidents: 38, severity: 'Medium' },
  { area: 'Oshodi', incidents: 25, severity: 'High' },
  { area: 'Third Mainland', incidents: 56, severity: 'Critical' },
  { area: 'Apapa', incidents: 31, severity: 'Medium' },
];

const inflationData = [
  { month: 'Oct', rice: 65000, beans: 42000 },
  { month: 'Nov', rice: 68000, beans: 44000 },
  { month: 'Dec', rice: 75000, beans: 48000 },
  { month: 'Jan', rice: 78000, beans: 50000 },
  { month: 'Feb', rice: 75000, beans: 49500 },
];

export function PartnershipHub({ agencyName, onClose }: PartnershipHubProps) {
  const [activeTier, setActiveTier] = React.useState<'Basic' | 'Pro' | 'Enterprise'>('Pro');

  return (
    <div className="flex flex-col h-full bg-zinc-50 overflow-hidden">
      {/* Header */}
      <div className="bg-white border-b border-zinc-200 px-6 py-4 flex items-center justify-between sticky top-0 z-10">
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center text-white">
            <Handshake className="w-6 h-6" />
          </div>
          <div>
            <h2 className="text-sm font-black text-zinc-900 uppercase tracking-tight">Official Partnership Hub</h2>
            <div className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
              <p className="text-[10px] font-bold text-zinc-500 uppercase">{agencyName} • Verified Agency</p>
            </div>
          </div>
        </div>
        <button onClick={onClose} className="p-2 hover:bg-zinc-100 rounded-xl transition-colors">
          <X className="w-5 h-5 text-zinc-400" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-6 space-y-6">
        {/* Tier Selector */}
        <div className="bg-zinc-900 rounded-3xl p-6 text-white overflow-hidden relative">
          <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/10 blur-3xl rounded-full -mr-32 -mt-32" />
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-black uppercase tracking-tight italic">Premium Data API</h3>
                <p className="text-[10px] text-zinc-400 font-bold uppercase tracking-widest">Active Tier: {activeTier}</p>
              </div>
              <ShieldCheck className="w-8 h-8 text-blue-400" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              {['Basic', 'Pro', 'Enterprise'].map((tier) => (
                <button
                  key={tier}
                  onClick={() => setActiveTier(tier as any)}
                  className={`p-3 rounded-2xl border transition-all text-left ${
                    activeTier === tier 
                      ? 'bg-blue-600 border-blue-500 shadow-lg shadow-blue-600/20' 
                      : 'bg-zinc-800 border-zinc-700 hover:border-zinc-600'
                  }`}
                >
                  <p className="text-[10px] font-black uppercase tracking-widest mb-1">{tier}</p>
                  <p className="text-[9px] font-medium text-zinc-400 leading-tight">
                    {tier === 'Basic' ? 'Neighborhood Stats' : tier === 'Pro' ? 'Real-time Heatmaps' : 'Predictive Modeling'}
                  </p>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Insight Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Traffic Intensity Heatmap */}
          <div className="bg-white rounded-3xl border border-zinc-200 p-6 shadow-sm">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-red-50 flex items-center justify-center text-red-600">
                  <Activity className="w-4 h-4" />
                </div>
                <h4 className="text-[10px] font-black text-zinc-400 uppercase tracking-widest">Incident Intensity</h4>
              </div>
              <span className="text-[9px] font-black text-red-600 bg-red-50 px-2 py-0.5 rounded uppercase">Live Data</span>
            </div>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={trafficHotspots} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f4f4f5" />
                  <XAxis type="number" hide />
                  <YAxis 
                    dataKey="area" 
                    type="category" 
                    width={80} 
                    axisLine={false} 
                    tickLine={false} 
                    tick={{ fontSize: 10, fontWeight: 700, fill: '#71717a' }}
                  />
                  <Tooltip 
                    cursor={{ fill: '#f8fafc' }}
                    contentStyle={{ borderRadius: '16px', border: 'none', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)' }}
                  />
                  <Bar dataKey="incidents" radius={[0, 4, 4, 0]}>
                    {trafficHotspots.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.severity === 'Critical' ? '#ef4444' : entry.severity === 'High' ? '#f97316' : '#3b82f6'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Market Price Trends */}
          <div className="bg-white rounded-3xl border border-zinc-200 p-6 shadow-sm">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-green-50 flex items-center justify-center text-green-600">
                  <TrendingUp className="w-4 h-4" />
                </div>
                <h4 className="text-[10px] font-black text-zinc-400 uppercase tracking-widest">Price Inflation Index</h4>
              </div>
              <span className="text-[9px] font-black text-green-600 bg-green-50 px-2 py-0.5 rounded uppercase">Verified NBS</span>
            </div>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={inflationData}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f4f4f5" />
                  <XAxis 
                    dataKey="month" 
                    axisLine={false} 
                    tickLine={false} 
                    tick={{ fontSize: 10, fontWeight: 700, fill: '#71717a' }} 
                  />
                  <YAxis hide domain={['auto', 'auto']} />
                  <Tooltip 
                    contentStyle={{ borderRadius: '16px', border: 'none', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)' }}
                  />
                  <Line type="monotone" dataKey="rice" stroke="#10b981" strokeWidth={3} dot={{ r: 4, fill: '#10b981' }} />
                  <Line type="monotone" dataKey="beans" stroke="#3b82f6" strokeWidth={3} dot={{ r: 4, fill: '#3b82f6' }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Predictive Alerts */}
        <div className="bg-white rounded-3xl border border-zinc-200 p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-[10px] font-black text-zinc-400 uppercase tracking-widest flex items-center gap-2">
              <Zap className="w-3.5 h-3.5 text-amber-500" />
              Predictive Disaster Alerts
            </h4>
            <span className="text-[8px] font-black bg-zinc-100 px-1.5 py-0.5 rounded uppercase">AI Model v2.4</span>
          </div>
          <div className="space-y-3">
            {[
              { title: 'Flood Risk: Lekki Phase 1', desc: '92% probability of flash flood in next 48h based on rainfall and sensor data.', status: 'High', color: 'text-red-600 bg-red-50' },
              { title: 'Gridlock Warning: Third Mainland', desc: 'Projected 4-hour delay starting 4PM due to scheduled maintenance and high volume.', status: 'Medium', color: 'text-amber-600 bg-amber-50' },
            ].map((alert, i) => (
              <div key={i} className={`p-4 rounded-2xl border border-zinc-100 ${alert.color}`}>
                <div className="flex justify-between items-start mb-1">
                  <h5 className="text-xs font-bold">{alert.title}</h5>
                  <span className="text-[8px] font-black uppercase px-1.5 py-0.5 rounded bg-white border border-current">{alert.status}</span>
                </div>
                <p className="text-[10px] font-medium leading-relaxed opacity-80">{alert.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Data Access Terminal */}
        <div className="bg-zinc-50 rounded-3xl border-2 border-dashed border-zinc-200 p-8 flex flex-col items-center text-center">
          <div className="w-16 h-16 rounded-3xl bg-white shadow-sm flex items-center justify-center mb-4">
            <Database className="w-8 h-8 text-zinc-300" />
          </div>
          <h4 className="text-sm font-black text-zinc-900 uppercase">Raw Data Export</h4>
          <p className="text-[10px] text-zinc-500 font-bold mb-6 max-w-[200px]">Download aggregated neighborhood reports in CSV/JSON format for internal modeling.</p>
          <button className="flex items-center gap-2 bg-zinc-900 text-white px-6 py-3 rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-zinc-800 transition-all active:scale-95 shadow-lg shadow-zinc-900/10">
            <ArrowUpRight className="w-4 h-4" />
            Generate Export
          </button>
        </div>
      </div>

      {/* Footer Navigation */}
      <div className="bg-white border-t border-zinc-200 p-4 flex justify-between items-center px-8">
        {[
          { icon: BarChart3, label: 'Analytics' },
          { icon: Map, label: 'Heatmaps' },
          { icon: Globe, label: 'API' },
          { icon: FileText, label: 'Reports' },
        ].map((item, i) => (
          <button key={i} className="flex flex-col items-center gap-1 group">
            <item.icon className={`w-5 h-5 ${i === 0 ? 'text-blue-600' : 'text-zinc-400 group-hover:text-zinc-600'} transition-colors`} />
            <span className={`text-[8px] font-black uppercase tracking-tighter ${i === 0 ? 'text-blue-600' : 'text-zinc-400'}`}>{item.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

import { X, Handshake } from 'lucide-react';
